import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Button, Tooltip } from "antd";
import {
  getDirectComp,
  getTotalDirectComp,
} from "../redux/actions/CompanyAction";
import { emdash, toDollar } from "../utils/Constants/systemSetting";
import { exportToCSV } from "../utils/ExecutiveData/csvExport";
import { CitationLink } from "./CitationLink";

export default function TargetTotalDirectCompensation({
  companyInfo,
  reportYear,
  PDFprocessing,
  setReportFiscalYear,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.UserReducer);
  const { subscription } = useSelector((state) => state.CustomerReducer);
  const isTier2 = useMemo(() => {
    if (user?.tier2access || subscription?.plan?.metadata?.isTier2 === "true") {
      return true;
    }
    return false;
  }, [user, subscription]);

  function addSpaceBetweenCapitalizedWords(str) {
    return str.replace(/([a-z])([A-Z])/g, "$1 $2");
  }

  const { totalDirectComp } = useSelector((state) => state.CompanyReducer);

  useEffect(() => {
    const token = localStorage.getItem("ACCESS_TOKEN");
    const ticker = companyInfo?.Ticker;
    const CIK = companyInfo?.CIK;

    if (ticker) {
      dispatch(
        getTotalDirectComp(
          JSON.stringify([{ ticker, CIK }]),
          reportYear,
          token,
        ),
      );
    }
  }, [companyInfo, reportYear, dispatch]);

  const CompanyTargetTotalDirectCompensation =
    totalDirectComp?.[companyInfo?.Ticker];

  const DirectCompColumns = useMemo(() => {
    if (!CompanyTargetTotalDirectCompensation?.DirectComp) return [];
    const compensationColumns = [];
    CompanyTargetTotalDirectCompensation.DirectComp.forEach((executiveData) =>
      Object.keys(executiveData)
        .filter((item) => item !== "LtiSplit")
        .forEach((tableKey) => {
          if (
            !compensationColumns.some((item) => item.key === tableKey) &&
            tableKey !== "Awards"
          ) {
            compensationColumns.push({
              title: (
                <Tooltip
                  title={
                    tableKey.includes("Percent") ? "Percent of Base Salary" : ""
                  }
                >
                  <span
                    style={{
                      cursor: tableKey.includes("Percent") ? "help" : "",
                    }}
                  >
                    {addSpaceBetweenCapitalizedWords(tableKey).replace(
                      "Lti ",
                      "LTI ",
                    )}
                  </span>
                </Tooltip>
              ),
              dataIndex: tableKey,
              key: tableKey,
              align: "center",
              render: (item, col) => {
                return !isNaN(item) &&
                  (tableKey?.includes("Amount") ||
                    tableKey?.includes("Comp") ||
                    tableKey?.includes("Salary"))
                  ? toDollar(item)
                  : item;
              },
            });
          }
          const awards = executiveData?.Awards || [];
          awards.forEach((award) => {
            Object.entries(award).forEach(([awardKey, awardVal]) => {
              if (!compensationColumns.some((item) => item.key === awardKey)) {
                compensationColumns.push({
                  title:
                    addSpaceBetweenCapitalizedWords(awardKey) +
                    ((!awardKey.includes("Date") ||
                      awardKey.includes("Award")) &&
                    !awardKey.includes("Type")
                      ? CompanyTargetTotalDirectCompensation.DirectComp?.some(
                          (exec) =>
                            exec?.Awards?.some((award) =>
                              Object.entries(award).some(([key, val]) => {
                                return key === awardKey && val?.isDollarAmount;
                              }),
                            ),
                        )
                        ? CompanyTargetTotalDirectCompensation.DirectComp?.some(
                            (exec) =>
                              exec?.Awards?.some((award) =>
                                Object.entries(award).some(([key, val]) => {
                                  return (
                                    key === awardKey && val?.isPricePerShare
                                  );
                                }),
                              ),
                          )
                          ? " ($/share)"
                          : " ($)"
                        : " (#)"
                      : ""),
                  dataIndex: awardKey,
                  key: awardKey,
                  align: "center",
                  isAward: true,
                });
              }
            });
          });
        }),
    );
    return compensationColumns;
  }, [CompanyTargetTotalDirectCompensation]);

  const LTIsplitColumns = useMemo(() => {
    if (
      !CompanyTargetTotalDirectCompensation?.DirectComp ||
      !CompanyTargetTotalDirectCompensation?.DirectComp.some(
        (item) => item?.LtiSplit,
      )
    )
      return [];
    const compensationColumns = [];
    compensationColumns.push({
      title: "Executive Name",
      dataIndex: "ExecutiveName",
      key: "ExecutiveName",
      align: "center",
    });

    CompanyTargetTotalDirectCompensation.DirectComp.forEach((executiveData) => {
      Object.keys(executiveData?.LtiSplit || {}).forEach((tableKey) => {
        if (!compensationColumns.some((item) => item.key === tableKey)) {
          compensationColumns.push({
            title: addSpaceBetweenCapitalizedWords(tableKey)
              .replace("Lti ", "LTI ")
              .replace("Psus", "PSUs")
              .replace("Rsus", "RSUs"),
            dataIndex: tableKey,
            key: tableKey,
            align: "center",
            render: (item) => (!item || isNaN(item) ? item : toDollar(item)),
          });
        }
        const awards = executiveData?.Awards || [];
        awards.forEach((award) => {
          Object.entries(award).forEach(([awardKey, awardVal]) => {
            if (!compensationColumns.some((item) => item.key === awardKey)) {
              compensationColumns.push({
                title:
                  addSpaceBetweenCapitalizedWords(awardKey) +
                  ((!awardKey.includes("Date") || awardKey.includes("Award")) &&
                  !awardKey.includes("Type")
                    ? CompanyTargetTotalDirectCompensation.DirectComp?.some(
                        (exec) =>
                          exec?.Awards?.some((award) =>
                            Object.entries(award).some(([key, val]) => {
                              return key === awardKey && val?.isDollarAmount;
                            }),
                          ),
                      )
                      ? CompanyTargetTotalDirectCompensation.DirectComp?.some(
                          (exec) =>
                            exec?.Awards?.some((award) =>
                              Object.entries(award).some(([key, val]) => {
                                return key === awardKey && val?.isPricePerShare;
                              }),
                            ),
                        )
                        ? " ($/share)"
                        : " ($)"
                      : " (#)"
                    : ""),
                dataIndex: awardKey,
                key: awardKey,
                align: "center",

                isAward: true,
              });
            }
          });
        });
      });
    });
    return compensationColumns;
  }, [CompanyTargetTotalDirectCompensation]);

  const DirectCompData = useMemo(() => {
    if (!CompanyTargetTotalDirectCompensation?.DirectComp) {
      return [];
    }

    const allData = [];
    CompanyTargetTotalDirectCompensation.DirectComp.forEach((award) => {
      const newRow = {};

      DirectCompColumns.forEach((key) => {
        if (key?.isAward) {
          newRow[key.dataIndex] =
            typeof award?.[key.dataIndex] === "string" ? (
              award?.[key.dataIndex]
            ) : award?.[key.dataIndex] &&
              award?.[key.dataIndex] !== undefined ? (
              award?.[key.dataIndex]
            ) : award?.[key.dataIndex] &&
              award?.[key.dataIndex] !== undefined ? (
              <div>
                <p>
                  Threshold:&nbsp;
                  {award?.[key.dataIndex] !== undefined
                    ? award?.[key.dataIndex]
                    : emdash}
                </p>
                <p>
                  Target:&nbsp;
                  {award?.[key.dataIndex]}
                </p>
                <p>
                  Maximum:&nbsp;
                  {award?.[key.dataIndex] !== undefined
                    ? award?.[key.dataIndex]
                    : emdash}
                </p>
              </div>
            ) : (
              emdash
            );
        } else {
          newRow[key.dataIndex] = award?.[key.dataIndex]
            ? award?.[key.dataIndex]
            : CompanyTargetTotalDirectCompensation.DirectComp.some(
                  (otherExec) => {
                    return otherExec?.[key.dataIndex];
                  },
                )
              ? emdash
              : award?.[key.dataIndex]
                ? award?.[key.dataIndex]
                : emdash;
        }
      });
      allData.push(newRow);
    });
    return allData;
  }, [CompanyTargetTotalDirectCompensation, DirectCompColumns]);

  const LTIsplitData = useMemo(() => {
    if (
      !CompanyTargetTotalDirectCompensation?.DirectComp ||
      !CompanyTargetTotalDirectCompensation?.DirectComp.some(
        (item) => item?.LtiSplit,
      )
    ) {
      return [];
    }

    const allData = [];
    CompanyTargetTotalDirectCompensation.DirectComp.forEach(
      (allAwardDetails) => {
        const newRow = {};

        const award = allAwardDetails?.LtiSplit;

        LTIsplitColumns.forEach((key) => {
          if (key?.isAward) {
            newRow[key.dataIndex] =
              typeof award?.[key.dataIndex] === "string" ? (
                award?.[key.dataIndex]
              ) : award?.[key.dataIndex] &&
                award?.[key.dataIndex] !== undefined ? (
                award?.[key.dataIndex]
              ) : award?.[key.dataIndex] &&
                award?.[key.dataIndex] !== undefined ? (
                <div>
                  <p>
                    Threshold:&nbsp;
                    {award?.[key.dataIndex] !== undefined
                      ? award?.[key.dataIndex]
                      : emdash}
                  </p>
                  <p>
                    Target:&nbsp;
                    {award?.[key.dataIndex]}
                  </p>
                  <p>
                    Maximum:&nbsp;
                    {award?.[key.dataIndex] !== undefined
                      ? award?.[key.dataIndex]
                      : emdash}
                  </p>
                </div>
              ) : (
                emdash
              );
          } else if (key?.dataIndex === "ExecutiveName") {
            newRow["ExecutiveName"] = allAwardDetails?.ExecutiveName;
          } else {
            newRow[key.dataIndex] = award?.[key.dataIndex]
              ? award?.[key.dataIndex]
              : CompanyTargetTotalDirectCompensation.DirectComp.some(
                    (otherExec) => {
                      return otherExec?.[key.dataIndex];
                    },
                  )
                ? emdash
                : award?.[key.dataIndex]
                  ? award?.[key.dataIndex]
                  : emdash;
          }
        });
        allData.push(newRow);
      },
    );
    return allData;
  }, [CompanyTargetTotalDirectCompensation, LTIsplitColumns]);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  useEffect(() => {
    if (
      isInitialLoad &&
      totalDirectComp?.[companyInfo?.Ticker] &&
      !totalDirectComp?.[companyInfo?.Ticker]?.Citation
    ) {
      setReportFiscalYear("FY 2024");
      setIsInitialLoad(false);
    }
  }, [totalDirectComp, companyInfo, setReportFiscalYear, isInitialLoad]);

  return (
    <>
      {!isTier2 ? (
        <div
          className="comp-ai-section col-lg-9 executives "
          style={{ height: "100%", padding: 50 }}
        >
          <h3
            style={{
              textAlign: "center",
              fontWeight: "normal",
              fontSize: 22,
            }}
          >
            <p style={{ fontWeight: "bold" }}>
              This page is for C-Suite Comp&reg; Enterprise users only.
            </p>
            <br />
            <p>
              Contact your account manager to access Total Direct Compensation.
            </p>
          </h3>
        </div>
      ) : (
        <>
          <div className="company-calculator-section col-lg-9 executives p-0">
            {!CompanyTargetTotalDirectCompensation?.Citation ? (
              <h2 style={{ textAlign: "center", marginTop: 50 }}>
                Total Direct Compensation for {companyInfo?.Ticker} Coming Soon
                for {reportYear}
              </h2>
            ) : !DirectCompColumns.length ? (
              <div>
                {" "}
                <p style={{ textAlign: "center", marginTop: 50, fontSize: 25 }}>
                  No Total Direct Compensation table found for{" "}
                  {companyInfo?.Ticker}{" "}
                  {reportYear.includes("FY") ? `in ${reportYear}` : ""}
                </p>
              </div>
            ) : (
              <div>
                <h2>{reportYear} Total Direct Compensation</h2>
                {/* {!PDFprocessing && (
                  <div style={{ width: "100%" }}>
                    <Button
                      style={{ marginBottom: 10 }}
                      onClick={() =>
                        exportToCSV(
                          formatDataForCSVexport(),
                          `${companyInfo?.Ticker} ${
                            CompanyTargetTotalDirectCompensation?.FiscalYear ||
                            ""
                          } Total Direct Compensation ${
                            new Date().toString().split("GMT-")[0]
                          }.csv`
                        )
                      }
                    >
                      Export Total Direct Compensation as CSV
                    </Button>
                  </div>
                )} */}
                <Table
                  columns={DirectCompColumns}
                  dataSource={DirectCompData}
                  pagination={{ hideOnSinglePage: true, pageSize: 25 }}
                />
                <div style={{ margin: 20 }} />
                <h2>LTI Split</h2>
                <Table
                  columns={LTIsplitColumns}
                  dataSource={LTIsplitData}
                  pagination={{ hideOnSinglePage: true, pageSize: 25 }}
                />
                {CompanyTargetTotalDirectCompensation?.ExplanationOfLtiSplitCalculation && (
                  <p style={{ marginTop: 10 }}>
                    <b>LTI Split Details: </b>
                    {
                      CompanyTargetTotalDirectCompensation?.ExplanationOfLtiSplitCalculation
                    }
                  </p>
                )}

                {CompanyTargetTotalDirectCompensation?.AdditionalDetails && (
                  <p style={{ marginTop: 10 }}>
                    <b>Additional Details: </b>

                    {CompanyTargetTotalDirectCompensation?.AdditionalDetails}
                  </p>
                )}
                {CompanyTargetTotalDirectCompensation?.Citation && (
                  <p style={{ marginTop: 10 }}>
                    <b>Citation: </b>
                    <CitationLink
                      citation={CompanyTargetTotalDirectCompensation?.Citation}
                      ticker={companyInfo?.Ticker}
                      reportYear={reportYear}
                    />
                  </p>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
